<template>
  <div ref="containerEl" class="scratch-ticket">
    <!-- drop-shadow on the wrapper follows the clipped notch outline;
         box-shadow would draw a plain rectangle. -->
    <div class="scratch-ticket__shadow">
      <div class="scratch-ticket__ticket">
        <!-- Revealed content (sits underneath the cover canvas). -->
        <div class="scratch-ticket__revealed">
          <div v-if="fields.noteLabel?.key" class="scratch-ticket__note">
            <TipTap v-model="noteValue" mode="single" />
          </div>
          <div v-if="fields.badgeLabel?.key" class="scratch-ticket__badge">
            <TipTap v-model="badgeValue" mode="single" />
          </div>
          <div v-if="fields.subtitleLabel?.key" class="scratch-ticket__subtitle">
            <TipTap v-model="subtitleValue" mode="single" />
          </div>
        </div>

        <!-- Cover canvas: painted with gradient + image + title; user
             scratches it away to reveal the content beneath. -->
        <canvas
          v-show="coverVisible"
          ref="coverCanvasEl"
          class="scratch-ticket__cover"
          :class="{ 'is-fading': isFading }"
          @pointerdown="onPointerDown"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointercancel="onPointerUp"
          @pointerleave="onPointerUp"
        />
      </div>
    </div>

    <!-- Hand hint sprite (canvas-drawn trail is rendered by the canvas
         itself; this SVG is just the cursor graphic that follows). -->
    <svg
      v-if="coverVisible && !isFading && showHandHint"
      class="scratch-ticket__hand"
      :width="handSizePx"
      :height="handHeightPx"
      viewBox="106.63 58.58 53.15 58.69"
      preserveAspectRatio="xMidYMin meet"
      :style="handStyle"
      aria-hidden="true"
    >
      <path :d="HAND_OUTLINE_D" fill="black" />
      <path :d="HAND_FILL_D" fill="white" />
    </svg>

    <!-- Countdown ring, post-reveal. Hidden while the cover is up (like the
         end user sees); in the editor it shows statically and half-filled so
         the author can style it without a real reveal. -->
    <div
      v-if="countdownRingVisible"
      class="scratch-ticket__countdown"
      :class="{ 'is-entering': countdownEntering }"
    >
      <svg
        :width="countdownSizePx"
        :height="countdownSizePx"
        :viewBox="`0 0 ${countdownSizePx} ${countdownSizePx}`"
        aria-hidden="true"
      >
        <circle
          class="scratch-ticket__countdown-fill"
          :cx="countdownSizePx / 2"
          :cy="countdownSizePx / 2"
          :r="countdownRadius"
        />
        <circle
          class="scratch-ticket__countdown-track"
          :cx="countdownSizePx / 2"
          :cy="countdownSizePx / 2"
          :r="countdownRadius"
          fill="none"
          :stroke-width="countdownStrokePx"
        />
        <circle
          class="scratch-ticket__countdown-progress"
          :cx="countdownSizePx / 2"
          :cy="countdownSizePx / 2"
          :r="countdownRadius"
          fill="none"
          :stroke-width="countdownStrokePx"
          :stroke-dasharray="countdownCircumference"
          :stroke-dashoffset="countdownDashOffset"
          :transform="`rotate(-90 ${countdownSizePx / 2} ${countdownSizePx / 2})`"
        />
      </svg>
      <div class="scratch-ticket__countdown-text">{{ countdownDisplay }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import type { ColorField, SizeField } from '@render/types/styles'

import {
  getColorStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '@render/composables/useStyleData'
import { useImagePreset } from '@render/composables/useImagePreset'
import { useTranslation } from '@render/composables/useTranslation'
import { useComponentProcessors } from '@render/composables/useComponentProcessors'
import { useEditorCommands } from '@render/composables/useEditorCommands'
import { PipeDisplayMode, usePipe } from '@render/composables/usePipe'
import TipTap from '@render/lib/TipTap'
import { tiptapToText } from '@render/lib/tiptapToText'
import { HAND_ASPECT, HAND_FILL_D, HAND_OUTLINE_D, HAND_TIP_NX, HAND_TIP_NY } from './handSprite'
import type { ScratchTicketFields as Model } from './definition'

const fields = defineModel<Model>('fields', {
  required: true,
  default: (): Model => ({}),
})

const emit = defineEmits<{
  (e: 'onReveal'): void
  (e: 'onTimerZero'): void
}>()

// Default values
const DEFAULT_NOTCH_POSITION_PCT = 24
const DEFAULT_TICKET_RADIUS_PCT = 4
const DEFAULT_BRUSH_SIZE_PX = 45
const DEFAULT_HAND_SIZE_PX = 80
const DEFAULT_HAND_ANIMATION_MS = 3000
const DEFAULT_COVER_IMAGE_WIDTH_PCT = 60
const DEFAULT_COVER_IMAGE_TOP_PCT = 50
const DEFAULT_COVER_TITLE_TOP_PCT = 15
const DEFAULT_COVER_TITLE_FONT_SIZE = '16px'
const DEFAULT_COVER_TITLE_WEIGHT = '900'
const DEFAULT_REVEAL_THRESHOLD_PCT = 30
const DEFAULT_AUTO_REVEAL_TIMEOUT_MS = 0
const DEFAULT_FADE_DURATION_MS = 600
const DEFAULT_COUNTDOWN_SIZE_PX = 60
const DEFAULT_COUNTDOWN_STROKE_PX = 3
const DEFAULT_COUNTDOWN_DURATION_S = 5
const DEFAULT_COUNTDOWN_TEXT_SIZE = '18px'
const DEFAULT_COUNTDOWN_TEXT_WEIGHT = '900'
const DEFAULT_BADGE_MARGIN_PX = 16
const DEFAULT_TICKET_WIDTH = '220px'
const DEFAULT_TICKET_HEIGHT = '300px'
const DEFAULT_PADDING = '24px'
const DEFAULT_MARGIN = '0'
const DEFAULT_NOTE_FONT_SIZE = '14px'
const DEFAULT_NOTE_WEIGHT = '400'
const DEFAULT_BADGE_FONT_SIZE = '48px'
const DEFAULT_BADGE_WEIGHT = '900'
const DEFAULT_SUBTITLE_FONT_SIZE = '16px'
const DEFAULT_SUBTITLE_WEIGHT = '900'
const DEFAULT_SHADOW_OFFSET_X = 0
const DEFAULT_SHADOW_OFFSET_Y = 8
const DEFAULT_SHADOW_BLUR_PX = 20

const MIN_BRUSH_SIZE_PX = 4
const MIN_HAND_SIZE_PX = 20
const MIN_HAND_ANIMATION_MS = 400
const MIN_NOTCH_POSITION_PCT = 8
const MAX_NOTCH_POSITION_PCT = 50
const MIN_TICKET_RADIUS_PCT = 0
const MAX_TICKET_RADIUS_PCT = 15
const MIN_COUNTDOWN_SIZE_PX = 20
const MIN_COUNTDOWN_STROKE_PX = 1
const MIN_COUNTDOWN_DURATION_S = 1
const MIN_COUNTDOWN_RADIUS_PX = 1

// Ticket clip-path geometry (fractions of the ticket box, 0-1).
const NOTCH_ARC_RX = 0.1062
const NOTCH_ARC_RY = 0.0797
const ARC_STEP_DEG = 7.5

// Gradient angles + cover-gradient mid stop.
const COVER_GRADIENT_ANGLE_DEG = 154
const COVER_GRADIENT_MID_STOP = 0.5048
const TICKET_GRADIENT_ANGLE_DEG = 135
const BADGE_GRADIENT_ANGLE_DEG = 60

// Reveal-threshold sampling.
const SAMPLE_GRID = 40
const SAMPLE_ALPHA_TRANSPARENT = 32

// Countdown ring.
const COUNTDOWN_RESERVE_BREATHING_PX = 6
const COUNTDOWN_EDITOR_IDLE_FILL = 0.5
const MS_PER_SECOND = 1000

// Editor preview timing.
const MIN_PREVIEW_ANIMATION_MS = 1000
const PREVIEW_ANIMATION_LOOPS = 2
const PREVIEW_COVER_DURATION_MS = 7000

// Base DS typography family token; falls back to the brand font if unset.
const FONT_FAMILY = 'var(--text-body-md-family, Avenir, sans-serif)'

// ---------- Translations ----------
const coverTitleValue = useTranslation(() => fields.value.coverTitleLabel?.key)
const noteValue = useTranslation(() => fields.value.noteLabel?.key)
const badgeValue = useTranslation(() => fields.value.badgeLabel?.key)
const subtitleValue = useTranslation(() => fields.value.subtitleLabel?.key)

// Plain-text title for canvas fillText: tiptapToText strips the rich
// formatting (bold/italic/color) that canvas can't render anyway.
const coverTitleText = computed(() => tiptapToText(coverTitleValue.value as object))

// ---------- Pipe / mode ----------
const pipe = usePipe()
const isEditor = computed(() => pipe.currentMode.value === PipeDisplayMode.EDITOR)

// Non-editor: cover shown until revealed. Editor: cover hidden by default
// so authors can edit the revealed content; preview commands toggle it on.
const editorCoverVisible = ref(false)
const isRevealed = ref(false)
const isFading = ref(false)
const coverVisible = computed(() => {
  if (isRevealed.value) return false
  return isEditor.value ? editorCoverVisible.value : true
})

// ---------- Ticket clip path ----------
function boxPercent(v: number): string {
  return `${+(v * 100).toFixed(3)}%`
}

function arcPoints(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  fromDeg: number,
  toDeg: number,
): string[] {
  const steps = rx > 0 && ry > 0 ? Math.ceil(Math.abs(toDeg - fromDeg) / ARC_STEP_DEG) : 0
  const points: string[] = []
  for (let i = 0; i <= steps; i++) {
    const rad = ((fromDeg + ((toDeg - fromDeg) * i) / Math.max(1, steps)) * Math.PI) / 180
    points.push(`${boxPercent(cx + rx * Math.cos(rad))} ${boxPercent(cy + ry * Math.sin(rad))}`)
  }
  return points
}

const ticketClipPath = computed(() => {
  // Floor at 8% (ceil(halfH*100)) so `top` stays >= 0; lower values push the
  // notch above the top edge and carve the corners instead of the side.
  const percent = Math.min(
    MAX_NOTCH_POSITION_PCT,
    Math.max(
      MIN_NOTCH_POSITION_PCT,
      Number(fields.value.notchPositionPercent ?? DEFAULT_NOTCH_POSITION_PCT),
    ),
  )
  // Notch half-height (matches the legacy ry) so cutouts keep their shape.
  const halfH = NOTCH_ARC_RY
  const top = percent / 100 - halfH
  const bot = percent / 100 + halfH
  // Author percent (0-15) to a normalized 0-1 radius, clamped so it can't
  // overlap the notch arcs. As box percentages it becomes rx/ry, so
  // corners go slightly oval on non-square tickets (negligible at small r).
  const requestedR =
    Math.max(
      MIN_TICKET_RADIUS_PCT,
      Math.min(
        MAX_TICKET_RADIUS_PCT,
        Number(fields.value.ticketBorderRadiusPercent ?? DEFAULT_TICKET_RADIUS_PCT),
      ),
    ) / 100
  const r = Math.max(0, Math.min(requestedR, top, 1 - bot))
  const notchY = percent / 100
  const points = [
    ...arcPoints(1 - r, r, r, r, -90, 0),
    ...arcPoints(1, notchY, NOTCH_ARC_RX, NOTCH_ARC_RY, 270, 90),
    ...arcPoints(1 - r, 1 - r, r, r, 0, 90),
    ...arcPoints(r, 1 - r, r, r, 90, 180),
    ...arcPoints(0, notchY, NOTCH_ARC_RX, NOTCH_ARC_RY, 90, -90),
    ...arcPoints(r, r, r, r, 180, 270),
  ]
  return `polygon(${points.join(', ')})`
})

// ---------- Cover canvas ----------
const containerEl = ref<HTMLDivElement | null>(null)
const coverCanvasEl = ref<HTMLCanvasElement | null>(null)
let coverCtx: CanvasRenderingContext2D | null = null
let resizeObserver: ResizeObserver | null = null

const userHasInteracted = ref(false)
const handPos = ref({ x: 0, y: 0 })

const brushRadius = computed(() =>
  Math.max(MIN_BRUSH_SIZE_PX, Number(fields.value.brushSize ?? DEFAULT_BRUSH_SIZE_PX)),
)
const handSizePx = computed(() =>
  Math.max(MIN_HAND_SIZE_PX, Number(fields.value.handSize ?? DEFAULT_HAND_SIZE_PX)),
)
const handHeightPx = computed(() => Math.round(handSizePx.value * HAND_ASPECT))

// Hand hint visibility: enabled by default in non-editor modes; in
// editor only during a previewAnimation run.
const handHintActive = ref(false)
const showHandHint = computed(() => {
  if (fields.value.handHintEnabled === false) return false
  if (userHasInteracted.value) return false
  return !isEditor.value || handHintActive.value
})

// ---------- Cover image loading ----------
const coverImagePreset = useImagePreset(() => fields.value.coverImage)
const coverImageEl = ref<HTMLImageElement | null>(null)
const coverImageLoadedSrc = ref<string | null>(null)
// Most recently requested src. An older in-flight load that resolves out of
// order (fast artwork swap / slow network) checks this and bails, so a stale
// image can't overwrite a newer cover. Instance-scoped (one per <script setup>).
let latestCoverSrc: string | null = null

watch(
  () => coverImagePreset.value?.imgAttrs.src,
  (src) => {
    // SSR (nuxt generate prerender): no Image constructor / canvas in Node.
    // The cover is painted client-side from onMounted
    if (typeof window === 'undefined') return

    latestCoverSrc = src ?? null
    if (!src) {
      coverImageEl.value = null
      coverImageLoadedSrc.value = null
      paintCover()
      return
    }
    if (src === coverImageLoadedSrc.value && coverImageEl.value) return
    const img = new Image()
    // Credentialed CORS: sends the auth cookie (media is cookie-protected and
    // cross-origin on staging/prod) AND keeps the canvas untainted so
    // getImageData (threshold sampling) works. The server returns
    // Access-Control-Allow-Credentials with a specific origin. 'anonymous'
    // would drop the cookie and 401 cross-origin.
    img.crossOrigin = 'use-credentials'
    img.onload = () => {
      if (src !== latestCoverSrc) return
      coverImageEl.value = img
      coverImageLoadedSrc.value = src
      paintCover()
    }
    img.onerror = () => {
      if (src !== latestCoverSrc) return
      // Fall back to gradient + title only if the image fails to load
      // (or CORS rejects it).
      coverImageEl.value = null
      coverImageLoadedSrc.value = null
      paintCover()
    }
    img.src = src
  },
  { immediate: true },
)

// ---------- Color resolution helpers ----------
function col(f: ColorField | undefined, fallback: string) {
  return getColorStyleData(f) || fallback
}
function sz(f: SizeField | undefined, fallback: string) {
  return getSizeStyleData(f) || fallback
}

// Canvas can't parse `var(...)` (fillStyle / ctx.font shorthand). Resolve a CSS
// property to a concrete value by reading it back off a hidden host element.
function resolveCssVar(
  property: 'color' | 'fontWeight' | 'fontFamily',
  value: string,
  el: HTMLElement,
  fallback: string,
): string {
  if (!value.startsWith('var(')) return value
  const measureEl = document.createElement('span')
  measureEl.style[property] = value
  measureEl.style.display = 'none'
  el.appendChild(measureEl)
  const resolved = getComputedStyle(measureEl)[property]
  measureEl.remove()
  return resolved || fallback
}

// ---------- Canvas paint ----------
function paintCover() {
  const canvas = coverCanvasEl.value
  const ctx = coverCtx
  if (!canvas || !ctx) return

  // Coordinate system is the DPR-scaled buffer in DEVICE pixels:
  // canvas.width/height are the buffer dims set in resizeCanvasToContainer,
  // not CSS px. Any length sourced from CSS px must be multiplied by dpr.
  const w = canvas.width
  const h = canvas.height
  if (w === 0 || h === 0) return

  ctx.globalCompositeOperation = 'source-over'
  ctx.clearRect(0, 0, w, h)

  // 1. Background gradient (start → mid → end, 154°).
  const start = resolveCssVar(
    'color',
    col(fields.value.coverBgColorStart, 'var(--color-background-subtle)'),
    canvas,
    '#DAF1ED',
  )
  const mid = resolveCssVar(
    'color',
    col(fields.value.coverBgColorMid, 'var(--color-background-surface)'),
    canvas,
    '#FFFFFF',
  )
  const end = resolveCssVar(
    'color',
    col(fields.value.coverBgColorEnd, 'var(--color-background-subtle)'),
    canvas,
    '#DAF1ED',
  )
  // CSS gradient angles run clockwise from +Y, so shift 90° and draw the
  // gradient along that diagonal across the canvas.
  const rad = (COVER_GRADIENT_ANGLE_DEG - 90) * (Math.PI / 180)
  const cx = w / 2
  const cy = h / 2
  const halfDiag = Math.sqrt(w * w + h * h) / 2
  const dx = Math.cos(rad) * halfDiag
  const dy = Math.sin(rad) * halfDiag
  const grad = ctx.createLinearGradient(cx - dx, cy - dy, cx + dx, cy + dy)
  grad.addColorStop(0, start)
  grad.addColorStop(COVER_GRADIENT_MID_STOP, mid)
  grad.addColorStop(1, end)
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, w, h)

  // 2. Cover image (if loaded), centered horizontally, vertical center
  //    at coverImageTopPercent % from top, width = coverImageWidthPercent
  //    % of canvas, height auto from natural aspect.
  if (coverImageEl.value) {
    const img = coverImageEl.value
    const widthPct = Math.min(
      100,
      Math.max(0, Number(fields.value.coverImageWidthPercent ?? DEFAULT_COVER_IMAGE_WIDTH_PCT)),
    )
    const topPct = Math.min(
      100,
      Math.max(0, Number(fields.value.coverImageTopPercent ?? DEFAULT_COVER_IMAGE_TOP_PCT)),
    )
    const drawW = w * (widthPct / 100)
    const aspect = img.naturalHeight > 0 ? img.naturalHeight / img.naturalWidth : 1
    const drawH = drawW * aspect
    const drawX = (w - drawW) / 2
    const drawY = h * (topPct / 100) - drawH / 2
    ctx.drawImage(img, drawX, drawY, drawW, drawH)
  }

  // 3. Title text (if translation resolves to non-empty plaintext).
  const titleText = coverTitleText.value
  if (titleText) {
    const color = resolveCssVar(
      'color',
      col(fields.value.coverTitleColor, 'var(--color-text-primary)'),
      canvas,
      '#0B3E53',
    )
    const fontSizePx =
      parseFloat(sz(fields.value.coverTitleFontSize, DEFAULT_COVER_TITLE_FONT_SIZE)) ||
      parseFloat(DEFAULT_COVER_TITLE_FONT_SIZE)
    const topPct = Math.min(
      100,
      Math.max(0, Number(fields.value.coverTitleTopPercent ?? DEFAULT_COVER_TITLE_TOP_PCT)),
    )
    // fontSizePx is CSS px; the canvas paints in device px, so scale by dpr
    // (mirrors the `brushRadius * dpr` convention in the scratch/erase code).
    const dpr = window.devicePixelRatio || 1
    // Resolve a possible var() weight to a concrete value for canvas.
    const weight = resolveCssVar(
      'fontWeight',
      fields.value.coverTitleFontWeight ?? DEFAULT_COVER_TITLE_WEIGHT,
      canvas,
      DEFAULT_COVER_TITLE_WEIGHT,
    )
    const family = resolveCssVar('fontFamily', FONT_FAMILY, canvas, 'Avenir, sans-serif')
    ctx.fillStyle = color
    ctx.font = `${weight} ${fontSizePx * dpr}px ${family}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(titleText, w / 2, h * (topPct / 100))
  }
}

function eraseAt(x: number, y: number, radius: number) {
  const ctx = coverCtx
  if (!ctx) return
  ctx.globalCompositeOperation = 'destination-out'
  ctx.beginPath()
  ctx.arc(x, y, radius, 0, Math.PI * 2)
  ctx.fill()
}

function resizeCanvasToContainer() {
  const canvas = coverCanvasEl.value
  if (!canvas) return
  const dpr = window.devicePixelRatio || 1
  const w = Math.max(1, Math.round(canvas.clientWidth * dpr))
  const h = Math.max(1, Math.round(canvas.clientHeight * dpr))
  if (canvas.width === w && canvas.height === h && coverCtx) return
  canvas.width = w
  canvas.height = h
  coverCtx = canvas.getContext('2d')
  if (coverCtx) {
    coverCtx.scale(1, 1)
    paintCover()
  }
}

function pointToCanvas(e: PointerEvent): [number, number] | null {
  const canvas = coverCanvasEl.value
  if (!canvas) return null
  const rect = canvas.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0) return null
  const x = ((e.clientX - rect.left) / rect.width) * canvas.width
  const y = ((e.clientY - rect.top) / rect.height) * canvas.height
  return [x, y]
}

// ---------- Pointer / scratch ----------
let pointerDown = false
let lastErase: [number, number] | null = null

function onPointerDown(e: PointerEvent) {
  if (isRevealed.value || isFading.value) return
  pointerDown = true
  const isFirstInteraction = !userHasInteracted.value
  userHasInteracted.value = true
  stopHandHint()
  // Hand-hint paints a trail on top of the cover; wipe before first erase.
  if (isFirstInteraction) paintCover()
  coverCanvasEl.value?.setPointerCapture(e.pointerId)
  const p = pointToCanvas(e)
  if (!p) return
  const dpr = window.devicePixelRatio || 1
  eraseAt(p[0], p[1], brushRadius.value * dpr)
  if (fields.value.revealOnRelease === false) scheduleRevealCheck()
}

function onPointerMove(e: PointerEvent) {
  if (!pointerDown || isRevealed.value || isFading.value) return
  const p = pointToCanvas(e)
  if (!p) return
  const ctx = coverCtx
  if (!ctx) return
  const dpr = window.devicePixelRatio || 1
  const r = brushRadius.value * dpr
  if (lastErase) {
    ctx.globalCompositeOperation = 'destination-out'
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.lineWidth = r * 2
    ctx.beginPath()
    ctx.moveTo(lastErase[0], lastErase[1])
    ctx.lineTo(p[0], p[1])
    ctx.stroke()
  }
  eraseAt(p[0], p[1], r)
  lastErase = p
  if (fields.value.revealOnRelease === false) scheduleRevealCheck()
}

function onPointerUp() {
  const wasDown = pointerDown
  pointerDown = false
  lastErase = null
  if (wasDown && fields.value.revealOnRelease !== false) scheduleRevealCheck()
}

// ---------- Threshold sampling ----------
let revealCheckScheduled = false
function scheduleRevealCheck() {
  if (revealCheckScheduled) return
  revealCheckScheduled = true
  requestAnimationFrame(() => {
    revealCheckScheduled = false
    if (isRevealed.value) return
    const pct = sampleRevealedPercent()
    const threshold = Math.max(
      0,
      Number(fields.value.revealThreshold ?? DEFAULT_REVEAL_THRESHOLD_PCT),
    )
    if (pct >= threshold) triggerReveal()
  })
}

function sampleRevealedPercent(): number {
  const canvas = coverCanvasEl.value
  const ctx = coverCtx
  if (!canvas || !ctx) return 0
  const cols = SAMPLE_GRID
  const rows = SAMPLE_GRID
  const stepX = Math.max(1, Math.floor(canvas.width / cols))
  const stepY = Math.max(1, Math.floor(canvas.height / rows))
  try {
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data
    let transparent = 0
    let total = 0
    for (let y = 0; y < canvas.height; y += stepY) {
      for (let x = 0; x < canvas.width; x += stepX) {
        const idx = (y * canvas.width + x) * 4 + 3
        if ((data[idx] ?? 255) < SAMPLE_ALPHA_TRANSPARENT) transparent++
        total++
      }
    }
    return total === 0 ? 0 : (transparent / total) * 100
  } catch {
    return 0
  }
}

// ---------- Reveal / reset ----------
let autoRevealTimer: ReturnType<typeof setTimeout> | null = null
let fadeTimer: ReturnType<typeof setTimeout> | null = null

function triggerReveal() {
  if (isRevealed.value || isFading.value) return
  stopHandHint()
  if (autoRevealTimer) {
    clearTimeout(autoRevealTimer)
    autoRevealTimer = null
  }
  isFading.value = true
  const duration = Math.max(
    0,
    Number(fields.value.autoRevealFadeDuration ?? DEFAULT_FADE_DURATION_MS),
  )
  fadeTimer = setTimeout(() => {
    isRevealed.value = true
    isFading.value = false
    fadeTimer = null
    if (!isEditor.value) {
      emit('onReveal')
      startCountdown()
    }
  }, duration)
}

function resetCover() {
  if (fadeTimer) {
    clearTimeout(fadeTimer)
    fadeTimer = null
  }
  if (autoRevealTimer) {
    clearTimeout(autoRevealTimer)
    autoRevealTimer = null
  }
  isFading.value = false
  isRevealed.value = false
  userHasInteracted.value = false
  lastErase = null
  resetCountdown()
  paintCover()
  scheduleAutoRevealTimer()
  if (!isEditor.value && fields.value.handHintEnabled !== false) startHandHint()
}

function scheduleAutoRevealTimer() {
  if (autoRevealTimer) {
    clearTimeout(autoRevealTimer)
    autoRevealTimer = null
  }
  const t = Number(fields.value.autoRevealTimeout ?? DEFAULT_AUTO_REVEAL_TIMEOUT_MS)
  if (!t || t <= 0) return
  if (isEditor.value) return
  autoRevealTimer = setTimeout(() => {
    autoRevealTimer = null
    triggerReveal()
  }, t)
}

// ---------- Hand hint loop ----------
const HINT_PAD_Y = 0.15
const HINT_PAD_X = 0.25
const HINT_WAVE_CYCLES = 3

function hintPathAt(t: number, w: number, h: number): { x: number; y: number } {
  const ampX = (w - w * HINT_PAD_X * 2) / 2
  const sweepY = h - h * HINT_PAD_Y * 2
  return {
    x: w / 2 + Math.sin(t * Math.PI * 2 * HINT_WAVE_CYCLES) * ampX,
    y: h * HINT_PAD_Y + t * sweepY,
  }
}

let hintRaf: number | null = null
let hintStart = 0
let hintLoopIndex = 0
let lastHintBufX: number | null = null
let lastHintBufY: number | null = null

function startHandHint() {
  if (hintRaf !== null) return
  hintStart = performance.now()
  hintLoopIndex = -1
  handHintActive.value = true
  lastHintBufX = null
  lastHintBufY = null
  paintCover()
  hintRaf = requestAnimationFrame(handHintFrame)
}

function stopHandHint() {
  handHintActive.value = false
  if (hintRaf !== null) {
    cancelAnimationFrame(hintRaf)
    hintRaf = null
  }
  lastHintBufX = null
  lastHintBufY = null
}

function handHintFrame(ts: number) {
  hintRaf = null
  if (!coverCanvasEl.value || userHasInteracted.value) return
  const duration = Math.max(
    MIN_HAND_ANIMATION_MS,
    Number(fields.value.handAnimationDuration ?? DEFAULT_HAND_ANIMATION_MS),
  )
  const elapsed = ts - hintStart
  const t = (elapsed % duration) / duration
  const currentLoop = Math.floor(elapsed / duration)
  if (currentLoop !== hintLoopIndex) {
    hintLoopIndex = currentLoop
    paintCover()
    lastHintBufX = null
    lastHintBufY = null
  }

  const canvas = coverCanvasEl.value
  const w = canvas.width
  const h = canvas.height
  const { x, y } = hintPathAt(t, w, h)

  const ctx = coverCtx
  if (ctx && lastHintBufX !== null && lastHintBufY !== null) {
    const dpr = window.devicePixelRatio || 1
    const trailColor = resolveCssVar(
      'color',
      col(fields.value.handTrailColor, 'var(--color-text-inverse)'),
      canvas,
      '#FFFFFF',
    )
    ctx.globalCompositeOperation = 'source-over'
    ctx.strokeStyle = trailColor
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.lineWidth = brushRadius.value * dpr
    ctx.beginPath()
    ctx.moveTo(lastHintBufX, lastHintBufY)
    ctx.lineTo(x, y)
    ctx.stroke()
  }
  lastHintBufX = x
  lastHintBufY = y

  const cssX = canvas.clientLeft + (x / w) * canvas.clientWidth
  const cssY = canvas.clientTop + (y / h) * canvas.clientHeight
  handPos.value = { x: cssX, y: cssY }

  hintRaf = requestAnimationFrame(handHintFrame)
}

const handStyle = computed(() => {
  const tipX = handSizePx.value * HAND_TIP_NX
  const tipY = handHeightPx.value * HAND_TIP_NY
  return {
    transform: `translate(${handPos.value.x - tipX}px, ${handPos.value.y - tipY}px)`,
  }
})

// ---------- Countdown ring ----------
const countdownSizePx = computed(() =>
  Math.max(MIN_COUNTDOWN_SIZE_PX, Number(fields.value.countdownSize ?? DEFAULT_COUNTDOWN_SIZE_PX)),
)
const countdownStrokePx = computed(() =>
  Math.max(
    MIN_COUNTDOWN_STROKE_PX,
    Number(fields.value.countdownStrokeWidth ?? DEFAULT_COUNTDOWN_STROKE_PX),
  ),
)
const countdownRadius = computed(() =>
  // Clamp to >= 1: a fat stroke on a small ring could otherwise drive the
  // radius to 0/negative, which makes the SVG <circle> (and its dasharray)
  // invalid and the ring vanish.
  Math.max(MIN_COUNTDOWN_RADIUS_PX, countdownSizePx.value / 2 - countdownStrokePx.value / 2 - 1),
)
// Room reserved below the ticket for the ring's lower half (it straddles the
// ticket's bottom edge). Tracks the ring size so any author-set size fits,
// instead of a fixed value that a large ring would overflow into the sibling.
const countdownReservePx = computed(
  () => Math.round(countdownSizePx.value / 2) + COUNTDOWN_RESERVE_BREATHING_PX,
)
const countdownCircumference = computed(() => 2 * Math.PI * countdownRadius.value)

const countdownRemainingMs = ref(0)
const countdownTotalMs = computed(
  () =>
    Math.max(
      MIN_COUNTDOWN_DURATION_S,
      Number(fields.value.countdownDuration ?? DEFAULT_COUNTDOWN_DURATION_S),
    ) * MS_PER_SECOND,
)
const countdownVisible = ref(false)
const countdownEntering = ref(false)
// True while the rAF loop is depleting the ring. False in the editor shows
// the static half-filled ring so the author can style track + arc.
const countdownRunning = ref(false)
let countdownRaf: number | null = null
let countdownStartTs = 0
let countdownZeroFired = false
// Set during previewCountdown so the rAF loop knows not to emit onTimerZero
// at completion (preview should NOT trigger admin-wired navigation).
let countdownIsPreview = false

// Enabled only on an explicit `true`, not `undefined`: the toggle shows an
// absent value as OFF, so `=== true` keeps toggle and ring in sync. Fresh
// drops are ON because drag-drop seeds `true` (defaultValue isn't applied on load).
const countdownEnabled = computed(() => fields.value.showCountdown === true)

// Mirrors the end user: hidden while the cover is up, shown once it's gone.
// In the editor it also shows statically so the author can style it.
const countdownRingVisible = computed(() => {
  if (!countdownEnabled.value) return false
  if (coverVisible.value) return false
  return countdownVisible.value || isEditor.value
})

// Effective remaining ms driving the ring fill + the centered number.
const countdownEffectiveRemainingMs = computed(() => {
  // Active loop (live reveal or Preview Countdown): the live remaining value.
  if (countdownRunning.value) return countdownRemainingMs.value
  // Idle in the editor: show a partially-depleted ring so the author can style
  // both the track and the progress arc (a full ring hides the track behind it).
  if (isEditor.value) return countdownTotalMs.value * COUNTDOWN_EDITOR_IDLE_FILL
  // Idle elsewhere (e.g. after a live countdown finishes): the real value.
  return countdownRemainingMs.value
})

const countdownDashOffset = computed(() => {
  const progress =
    countdownTotalMs.value > 0
      ? Math.min(1, Math.max(0, countdownEffectiveRemainingMs.value / countdownTotalMs.value))
      : 0
  return countdownCircumference.value * (1 - progress)
})

const countdownDisplay = computed(() =>
  Math.max(0, Math.ceil(countdownEffectiveRemainingMs.value / MS_PER_SECOND)),
)

function startCountdown() {
  if (!countdownEnabled.value) return
  countdownVisible.value = true
  countdownEntering.value = true
  // Drop the entering class after one frame so the CSS transition runs.
  requestAnimationFrame(() => requestAnimationFrame(() => (countdownEntering.value = false)))
  countdownIsPreview = false
  countdownRunning.value = true
  countdownRemainingMs.value = countdownTotalMs.value
  countdownZeroFired = false
  countdownStartTs = performance.now()
  if (countdownRaf !== null) cancelAnimationFrame(countdownRaf)
  countdownRaf = requestAnimationFrame(countdownFrame)
}

function countdownFrame(ts: number) {
  countdownRaf = null
  const elapsed = ts - countdownStartTs
  countdownRemainingMs.value = Math.max(0, countdownTotalMs.value - elapsed)
  if (countdownRemainingMs.value <= 0) {
    if (!countdownZeroFired) {
      countdownZeroFired = true
      // Preview-mode runs in the editor and must NOT fire the event the
      // admin wired to drive navigation - that would yank them off the page
      // mid-edit.
      if (!countdownIsPreview) emit('onTimerZero')
    }
    countdownRunning.value = false
    // Preview run: drop back to the static idle ring once it completes.
    if (countdownIsPreview) {
      countdownVisible.value = false
      countdownIsPreview = false
    }
    return
  }
  countdownRaf = requestAnimationFrame(countdownFrame)
}

function resetCountdown() {
  if (countdownRaf !== null) {
    cancelAnimationFrame(countdownRaf)
    countdownRaf = null
  }
  countdownVisible.value = false
  countdownEntering.value = false
  countdownRunning.value = false
  countdownRemainingMs.value = 0
  countdownZeroFired = false
  countdownIsPreview = false
}

function previewCountdown() {
  if (!isEditor.value) return
  if (!countdownEnabled.value) return
  // Reset any in-flight countdown so a second click restarts cleanly.
  if (countdownRaf !== null) {
    cancelAnimationFrame(countdownRaf)
    countdownRaf = null
  }
  countdownVisible.value = true
  countdownEntering.value = false
  countdownIsPreview = true
  countdownRunning.value = true
  countdownRemainingMs.value = countdownTotalMs.value
  countdownZeroFired = false
  countdownStartTs = performance.now()
  countdownRaf = requestAnimationFrame(countdownFrame)
}

// ---------- Editor preview commands ----------
let previewTimer: ReturnType<typeof setTimeout> | null = null

function previewAnimation() {
  if (!isEditor.value) return
  if (previewTimer) {
    clearTimeout(previewTimer)
    previewTimer = null
  }
  userHasInteracted.value = false
  isFading.value = false
  isRevealed.value = false
  editorCoverVisible.value = true
  requestAnimationFrame(() => {
    resizeCanvasToContainer()
    startHandHint()
  })
  const duration = Math.max(
    MIN_PREVIEW_ANIMATION_MS,
    Number(fields.value.handAnimationDuration ?? DEFAULT_HAND_ANIMATION_MS),
  )
  previewTimer = setTimeout(() => {
    stopHandHint()
    editorCoverVisible.value = false
    previewTimer = null
  }, duration * PREVIEW_ANIMATION_LOOPS)
}

function previewCover() {
  if (!isEditor.value) return
  if (previewTimer) {
    clearTimeout(previewTimer)
    previewTimer = null
  }
  stopHandHint()
  userHasInteracted.value = false
  isFading.value = false
  isRevealed.value = false
  editorCoverVisible.value = true
  requestAnimationFrame(() => {
    resizeCanvasToContainer()
    paintCover()
  })
  previewTimer = setTimeout(() => {
    editorCoverVisible.value = false
    previewTimer = null
  }, PREVIEW_COVER_DURATION_MS)
}

// ---------- Lifecycle ----------
onMounted(() => {
  resizeCanvasToContainer()
  if (containerEl.value && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      if (!userHasInteracted.value && !isRevealed.value) {
        resizeCanvasToContainer()
      }
    })
    resizeObserver.observe(containerEl.value)
  }
  if (!isEditor.value) {
    scheduleAutoRevealTimer()
    if (fields.value.handHintEnabled !== false) startHandHint()
  }
})

onBeforeUnmount(() => {
  stopHandHint()
  resetCountdown()
  if (autoRevealTimer) clearTimeout(autoRevealTimer)
  if (fadeTimer) clearTimeout(fadeTimer)
  if (previewTimer) clearTimeout(previewTimer)
  resizeObserver?.disconnect()
  resizeObserver = null
})

// Repaint when cover-affecting fields change in the editor.
watch(
  () => [
    fields.value.coverBgColorStart,
    fields.value.coverBgColorMid,
    fields.value.coverBgColorEnd,
    fields.value.coverTitleColor,
    fields.value.coverTitleFontSize,
    fields.value.coverTitleFontWeight,
    fields.value.coverTitleTopPercent,
    fields.value.coverImageWidthPercent,
    fields.value.coverImageTopPercent,
    coverTitleText.value,
  ],
  () => {
    if (!userHasInteracted.value && !isRevealed.value) paintCover()
  },
)

useComponentProcessors({
  reset: () => resetCover(),
  reveal: () => triggerReveal(),
})

useEditorCommands({
  previewAnimation: () => previewAnimation(),
  previewCover: () => previewCover(),
  previewCountdown: () => previewCountdown(),
})

// ---------- CSS bindings ----------
// Here we manipulate with the custom drop-shadow, but not default DS box-shadow
// due to it should follow the ticket's notches
function shadowDropFilter(): string {
  if (fields.value.shadowEnabled !== true) return 'none'
  const x = Number(fields.value.shadowOffsetX ?? DEFAULT_SHADOW_OFFSET_X)
  const y = Number(fields.value.shadowOffsetY ?? DEFAULT_SHADOW_OFFSET_Y)
  const blur = Math.max(0, Number(fields.value.shadowBlur ?? DEFAULT_SHADOW_BLUR_PX))
  const color = col(fields.value.shadowColor, 'rgba(11, 46, 59, 0.18)')
  return `drop-shadow(${x}px ${y}px ${blur}px ${color})`
}

const css = computed(() => {
  const ticketBgStart = col(fields.value.ticketBgColorStart, 'var(--color-background-surface)')
  const ticketBgEnd = col(fields.value.ticketBgColorEnd, 'var(--color-background-surface)')
  const badgeStart = col(fields.value.badgeBgColorStart, 'var(--color-brand-secondary)')
  const badgeEnd = col(fields.value.badgeBgColorEnd, 'var(--color-brand-secondary)')
  return {
    width: sz(fields.value.width, DEFAULT_TICKET_WIDTH),
    height: sz(fields.value.height, DEFAULT_TICKET_HEIGHT),
    padding: getSpacingStyleData(fields.value.padding, DEFAULT_PADDING),
    margin: getSpacingStyleData(fields.value.margin, DEFAULT_MARGIN),
    fontFamily: FONT_FAMILY,
    ticketClipPath: ticketClipPath.value,
    ticketBg: `linear-gradient(${TICKET_GRADIENT_ANGLE_DEG}deg, ${ticketBgStart} 0%, ${ticketBgEnd} 100%)`,
    noteColor: col(fields.value.noteColor, 'var(--color-text-primary)'),
    noteFontSize: sz(fields.value.noteFontSize, DEFAULT_NOTE_FONT_SIZE),
    noteFontWeight: fields.value.noteFontWeight || DEFAULT_NOTE_WEIGHT,
    badgeBg: `linear-gradient(${BADGE_GRADIENT_ANGLE_DEG}deg, ${badgeStart} 0%, ${badgeEnd} 100%)`,
    badgeColor: col(fields.value.badgeColor, 'var(--color-text-inverse)'),
    badgeFontSize: sz(fields.value.badgeFontSize, DEFAULT_BADGE_FONT_SIZE),
    badgeFontWeight: fields.value.badgeFontWeight || DEFAULT_BADGE_WEIGHT,
    badgeMarginTop: `${Math.max(0, Number(fields.value.badgeMarginTop ?? DEFAULT_BADGE_MARGIN_PX))}px`,
    badgeMarginBottom: `${Math.max(0, Number(fields.value.badgeMarginBottom ?? DEFAULT_BADGE_MARGIN_PX))}px`,
    subtitleColor: col(fields.value.subtitleColor, 'var(--color-text-primary)'),
    subtitleFontSize: sz(fields.value.subtitleFontSize, DEFAULT_SUBTITLE_FONT_SIZE),
    subtitleFontWeight: fields.value.subtitleFontWeight || DEFAULT_SUBTITLE_WEIGHT,
    fadeDuration: `${Math.max(0, Number(fields.value.autoRevealFadeDuration ?? DEFAULT_FADE_DURATION_MS))}ms`,
    shadow: shadowDropFilter(),
    countdownProgress: col(fields.value.countdownProgressColor, 'var(--color-brand-secondary)'),
    countdownTrack: col(fields.value.countdownTrackColor, 'var(--color-background-subtle)'),
    countdownTextColor: col(fields.value.countdownTextColor, 'var(--color-text-primary)'),
    countdownTextSize: sz(fields.value.countdownTextSize, DEFAULT_COUNTDOWN_TEXT_SIZE),
    countdownTextWeight: fields.value.countdownTextWeight || DEFAULT_COUNTDOWN_TEXT_WEIGHT,
    countdownReserve: `${countdownReservePx.value}px`,
  }
})
</script>

<style scoped lang="scss">
.scratch-ticket {
  position: relative;
  width: v-bind('css.width');
  margin: v-bind('css.margin');
  user-select: none;
  padding-bottom: v-bind('css.countdownReserve');
  box-sizing: content-box;
}

.scratch-ticket__shadow {
  filter: v-bind('css.shadow');
  width: 100%;
  height: v-bind('css.height');
}

.scratch-ticket__ticket {
  position: relative;
  width: 100%;
  height: 100%;
  background: v-bind('css.ticketBg');
  box-sizing: border-box;
  overflow: hidden;
  clip-path: v-bind('css.ticketClipPath');
}

.scratch-ticket__revealed {
  position: absolute;
  inset: 0;
  padding: v-bind('css.padding');
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  box-sizing: border-box;
}

.scratch-ticket__note {
  width: 100%;
  color: v-bind('css.noteColor');
  font-family: v-bind('css.fontFamily');
  font-size: v-bind('css.noteFontSize');
  font-weight: v-bind('css.noteFontWeight');
  line-height: 1.4;
  overflow-wrap: break-word;
  word-break: break-word;
}

.scratch-ticket__badge {
  align-self: center;
  max-width: calc(100% - 16px);
  margin-top: v-bind('css.badgeMarginTop');
  margin-bottom: v-bind('css.badgeMarginBottom');
  padding: 16px 24px;
  background: v-bind('css.badgeBg');
  border-radius: 12px;
  box-sizing: border-box;
  font-family: v-bind('css.fontFamily');
  font-size: v-bind('css.badgeFontSize');
  font-weight: v-bind('css.badgeFontWeight');
  line-height: 1.1;
  color: v-bind('css.badgeColor');
  :deep(*) {
    color: v-bind('css.badgeColor');
  }
}

.scratch-ticket__subtitle {
  width: 100%;
  color: v-bind('css.subtitleColor');
  font-family: v-bind('css.fontFamily');
  font-size: v-bind('css.subtitleFontSize');
  font-weight: v-bind('css.subtitleFontWeight');
  line-height: 1.4;
  overflow-wrap: break-word;
  word-break: break-word;
}

.scratch-ticket__cover {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  touch-action: none;
  cursor: crosshair;
  box-sizing: border-box;
  opacity: 1;
  transition: opacity v-bind('css.fadeDuration') ease;

  &.is-fading {
    opacity: 0;
    pointer-events: none;
  }
}

.scratch-ticket__hand {
  position: absolute;
  left: 0;
  top: 0;
  display: block;
  pointer-events: none;
  will-change: transform;
  filter: drop-shadow(0 2px 3px rgb(0 0 0 / 0.25));
}

.scratch-ticket__countdown {
  position: absolute;
  left: 50%;
  bottom: v-bind('css.countdownReserve');
  transform: translate(-50%, 50%);
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transition: opacity 350ms ease;

  &.is-entering {
    opacity: 0;
  }
}

.scratch-ticket__countdown-fill {
  fill: var(--color-background-surface, #ffffff);
}

.scratch-ticket__countdown-track {
  stroke: v-bind('css.countdownTrack');
}

.scratch-ticket__countdown-progress {
  stroke: v-bind('css.countdownProgress');
  transition: stroke-dashoffset 80ms linear;
}

.scratch-ticket__countdown-text {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: v-bind('css.fontFamily');
  font-weight: v-bind('css.countdownTextWeight');
  font-size: v-bind('css.countdownTextSize');
  color: v-bind('css.countdownTextColor');
}
</style>
